import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Card,
  CardContent,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useAPI } from "../api/api";
import {
  getAchBankAccountSchema,
  getPaymentMethods,
  getVirtualCard,
} from "../api/paymentMethodsService";
import {
  achTransactionsService,
  paymentsService,
} from "../api/stipendsServices";
import no_payments from "../assets/no_payments.svg";
import {
  formatCurrency,
  getLocalizedMessage,
  savePaymentMethodsData,
} from "../common/helpers";
import {
  DeprecatedPaymentMethodStatus,
  getDeprecatedPaymentMethodStatus,
} from "../common/DeprecatedPaymentMethod";
import TransactionsList from "../components/Payment/TransactionsList";
import LatestPayment from "../components/Payment/LatestPayment";
import PaymentsList from "../components/Payment/PaymentsList";
import StipendCard from "../components/Stipend/StipendCard";
import { setTransactionsData } from "../redux/slices/cardSlice";
import { setData } from "../redux/slices/stipendSlice";
import theme from "../theme";
import {
  ACHTransaction,
  AvailablePaymentMethod,
  PaymentMethodProp,
  PaymentMethodsResponse,
  PaymentType,
  VirtualCardResponse,
} from "../types/paymentTypes";
import { Payment } from "../types/stipendTypes";
import ManageCard from "./Card/ManageCard";
import { Box } from "@mui/system";
import { RootState } from "../redux/store";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import { VCC_PROVIDERS } from "../common/constants";

const PaymentsPage = () => {
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();
  const { t } = useTranslation();

  const {
    data: paymentsData,
    loading: loadingPayments,
    error: paymentsError,
  } = useAPI<Payment[]>(paymentsService(), []);

  // const {
  //   data: achData,
  //   loading: loadingACHTransactions,
  //   error: achError,
  // } = useAPI<ACHTransaction[]>(achTransactionsService(), []);

  const {
    data: cards,
    loading: cardsLoading,
    error: cardsError,
  } = useAPI<VirtualCardResponse[]>(getVirtualCard());

  const {
    data: paymentMethodsData,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
  } = useAPI<PaymentMethodsResponse | null>(getPaymentMethods());
  const navigate = useNavigate();

  // const {
  //   data: achBankAccountData,
  //   loading: loadingAchBankAccountData,
  //   error: errorAchBankAccount,
  // } = useAPI<AvailablePaymentMethod | null>(getAchBankAccountSchema());

  const onlyVCIsAllowed: boolean = useSelector((state: RootState) => {
    const paymentMethods =
      state.paymentMethods.data?.available_payment_methods ?? [];

    if (
      paymentMethods.length === 1 &&
      paymentMethods[0].method_provider_name === VCC_PROVIDERS.highnote
    ) {
      return true;
    }
    return false;
  });

  function updatePropertyNameForChildren(
    obj: PaymentMethodProp
  ): PaymentMethodProp {
    if (!obj.properties) return obj;

    const updatedProperties = Object.entries(obj.properties).reduce(
      (acc, [key, value]) => {
        // Create a shallow copy of value
        const valueCopy = { ...value };

        // Setting the key as property_name for the copied object
        valueCopy.property_name = key;

        // Recursive call to handle further nesting
        acc[key] = updatePropertyNameForChildren(valueCopy);

        return acc;
      },
      {} as Record<string, PaymentMethodProp>
    );

    return { ...obj, properties: updatedProperties };
  }

  const paymentsMessage = getLocalizedMessage(
    process.env.REACT_APP_PAYMENTS_MESSAGE
  );

  useEffect(() => {
    if (!paymentMethodsData) {
      return;
    }

    savePaymentMethodsData(paymentMethodsData, dispatch);
    // will never happen because all users must have a card account
    // if (paymentMethodsData?.latest_payment_account === null) {
    //   navigate("/payment-methods?isFirstTime=true", { replace: true });
    // }
  }, [paymentMethodsData]);

  useEffect(() => {
    if (!paymentMethodsData) {
      return;
    }

    const newPaymentMethodsData = JSON.parse(
      JSON.stringify(paymentMethodsData)
    );

    savePaymentMethodsData(newPaymentMethodsData, dispatch);
    // will never happen because all users must have a card account
    // if (paymentMethodsData?.latest_payment_account === null) {
    //   navigate("/payment-methods?isFirstTime=true", { replace: true });
    // }
  }, [paymentMethodsData]);

  useEffect(() => {
    if (!paymentsError) {
      return;
    }
    showBoundary(paymentsError);
  }, [paymentsError]);

  useEffect(() => {
    if (!paymentMethodsError) {
      return;
    }

    showBoundary(paymentMethodsError);
  }, [paymentMethodsError]);

  useEffect(() => {
    dispatch(setData(paymentsData ?? []));
  }, [paymentsData]);

  // useEffect(() => {
  //   dispatch(setTransactionsData(achData ?? []));
  // }, [achData]);

  function paymentAccountSkeleton() {
    return (
      <Skeleton
        data-test-id="payment-account-skeleton"
        variant="rounded"
        height={96}
        style={{ marginBottom: "10px" }}
      />
    );
  }

  function paymentRowsSkeleton() {
    return (
      <Paper data-test-id="payments-skeleton" elevation={0}>
        <Skeleton
          variant="rounded"
          height={88}
          style={{ marginBottom: "10px" }}
        />
      </Paper>
    );
  }

  function paymentsSectionTitleSkeleton() {
    return (
      <Skeleton
        variant="text"
        height={40}
        width={"30%"}
        sx={{
          mt: 2,
          mb: 1,
        }}
      />
    );
  }

  const paymentsSectionTitle = (title: string) => {
    if (isLoading()) return paymentsSectionTitleSkeleton();
    return sectionTitle(title);
  };

  const sectionTitle = (title: string) => {
    return (
      <Typography
        sx={{
          mt: 2,
          mb: 1,
        }}
        variant="subtitle2"
        color="text.secondary"
        fontWeight="600"
      >
        {title}
      </Typography>
    );
  };

  function newCardSkeleton() {
    return (
      <Paper data-test-id="payments-skeleton" elevation={0}>
        <Skeleton variant="rounded" height={144.5} />
      </Paper>
    );
  }

  function highnoteCard() {
    if (cardsLoading) return newCardSkeleton();
    if (!cards) return null;

    const highnoteCard = cards.find(
      (card) => card.provider.toLowerCase() === "highnote"
    );
    if (!highnoteCard) return null;
    return (
      <>
        <Typography
          color="text.secondary"
          variant="subtitle2"
          mb={1}
          fontWeight="600"
        >
          {t("nmible_virtual_card_new")}
        </Typography>

        <ManageCard data={highnoteCard} />
      </>
    );
  }

  function updatePaymentMethodIfDeprecated() {
    if (
      deprecationStatus ===
        DeprecatedPaymentMethodStatus.UPDATE_NEEDED_WITH_BALANCE ||
      deprecationStatus ===
        DeprecatedPaymentMethodStatus.UPDATE_NEEDED_WITH_NO_BALANCE
    ) {
      // Navigate to payment methods page for UPDATE_NEEDED scenarios
      navigate("/payment-methods", {
        replace: true,
      });
    }
  }
  const deprecationStatus = getDeprecatedPaymentMethodStatus(
    paymentMethodsData,
    cards ?? []
  );

  updatePaymentMethodIfDeprecated();

  return (
    <Paper
      elevation={0}
      style={{
        backgroundColor: "transparent",
        minHeight: "calc(100vh - 56px - 32px)",
        paddingBottom: "50px",
      }}
    >
      {/* Alerts */}
      {paymentsMessage && <Alert severity="warning">{paymentsMessage}</Alert>}
      {paymentMethodsData?.has_failed_payments && failedPaymentAlert()}

      {/* Deprecated Payment Method Alerts */}
      {deprecatedPaymentMethodAlert()}

      {/* New Card Balance */}
      {highnoteCard()}

      {/* Payments */}
      {paymentsSectionTitle(t("payments"))}
      {isLoading()
        ? paymentRowsSkeleton()
        : paymentsData?.length === 0
        ? emptyData()
        : paymentRows()}

      {/* Payment Method */}
      {!onlyVCIsAllowed && paymentsSectionTitle(t("paymentMethod_title"))}
      {paymentAccount(paymentMethodsData)}
    </Paper>
  );

  function isLoading() {
    return loadingPayments || cardsLoading; // || loadingACHTransactions
  }

  function paymentAccount(paymentMethodsData: PaymentMethodsResponse | null) {
    if (paymentMethodsLoading) return paymentAccountSkeleton();
    if (!paymentMethodsData?.latest_payment_account || onlyVCIsAllowed)
      return null;
    return (
      <LatestPayment
        paymentAccount={paymentMethodsData.latest_payment_account}
        failedPayment={paymentMethodsData.has_failed_payments}
      />
    );
  }

  function failedPaymentAlert() {
    return (
      <StipendCard
        icon={<ErrorOutlineIcon />}
        title={t("failedPaymentWarning_title")}
        description={t("failedPaymentWarning_description")}
        color="white"
        backgroundColor="#D32F2F"
        linkTo="/payment-methods?failedPayment=true"
      />
    );
  }

  function deprecatedPaymentMethodAlert() {
    if (isLoading()) return null;
    if (!cards || !deprecationStatus) return null;

    const checkbookCard = cards.find(
      (card) => card.provider.toLowerCase() === "checkbook"
    );
    if (!checkbookCard) return null;

    if (
      deprecationStatus === DeprecatedPaymentMethodStatus.ALERT_WITH_BALANCE
    ) {
      return (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="div">
            {t("deprecated_payment_alert_with_balance", {
              balance: formatCurrency(checkbookCard.balance ?? 0),
            })}
          </Typography>
        </Alert>
      );
    }

    if (
      deprecationStatus === DeprecatedPaymentMethodStatus.ALERT_WITH_NO_BALANCE
    ) {
      return (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="div">
            {t("deprecated_payment_alert_no_balance")}
          </Typography>
        </Alert>
      );
    }

    return null;
  }

  function paymentRows() {
    return <PaymentsList payments={paymentsData ?? []} />;
  }

  function emptyData() {
    return (
      <Card variant="outlined" sx={{ border: "none" }}>
        <CardContent>
          <Typography
            variant="body1"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "25vh",
              textAlign: "center",
            }}
          >
            {t("payments_empty")}
          </Typography>
        </CardContent>
      </Card>
    );
  }
};

export default PaymentsPage;
